import { useEffect, useRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Button, CircularProgress, Link, Stack, Typography, alpha, useTheme } from '@mui/material'
import { HowToRegOutlined, MarkEmailReadOutlined, PersonAddAltRounded } from '@mui/icons-material'

import { useSignup } from '../hooks/useSignup'
import usePersist from '../hooks/usePersist'
import useDialogStyles from '../hooks/useDialogStyles'
import { usePathContext } from '../context/path'
import AuthCard, { AuthError, AuthField, OrDivider, PasswordField } from '../components/auth/AuthCard'
import PersistLoginAlert from '../components/auth/PersistLoginAlert'
import PersistLoginCheckbox from '../components/auth/PersistLoginCheckbox'
import SignInWithGoogleButton from '../components/auth/SignInWithGoogleButton'

const PASSWORD_RULES = ['At least 8 characters', '1 lowercase letter', '1 uppercase letter', '1 number', '1 symbol']

const Signup = () => {
  const theme = useTheme()
  const { goldButtonSx } = useDialogStyles()
  const accent = theme.palette.secondary.main
  const { setTitle } = usePathContext()

  const { signup, error, isLoading, mailSent } = useSignup()
  const { persist, setPersist } = usePersist()
  const nameRef = useRef(null)
  const emailRef = useRef(null)
  const passwordRef = useRef(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    await signup(
      nameRef.current.value,
      emailRef.current.value.trim(),
      passwordRef.current.value.trim(),
      persist
    )
  }

  useEffect(() => {
    setTitle("Welcome")
  }, [setTitle])

  const linkSx = { color: accent, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }

  // ---------- after sign up: check your mail ----------
  if (mailSent) {
    return (
      <AuthCard icon={MarkEmailReadOutlined} title="Verify your email" subtitle="One more step to activate your account.">
        <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.6, color: 'text.secondary' }}>
          We've sent a link to your email address. Click it to verify the address and activate your account.
        </Typography>
        <Box
          sx={{
            mt: 2,
            p: 1.25,
            borderRadius: 2,
            fontSize: '0.83rem',
            fontWeight: 600,
            color: accent,
            backgroundColor: alpha(accent, 0.08),
            border: `1px solid ${alpha(accent, 0.3)}`,
          }}
        >
          The link expires in 15 minutes.
        </Box>
        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '0.85rem', color: 'text.secondary' }}>
          Already verified?{' '}
          <Link component={RouterLink} to="/login" sx={linkSx}>Log in</Link>
        </Typography>
      </AuthCard>
    )
  }

  // ---------- sign up form ----------
  return (
    <AuthCard icon={PersonAddAltRounded} title="Create account" subtitle="Sign up to get started.">
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <AuthField label="Username" id="username" inputRef={nameRef} autoComplete="username" autoFocus required />
          <AuthField label="Email address" id="email" type="email" inputRef={emailRef} autoComplete="email" required />
          <PasswordField
            label="Password"
            id="password"
            inputRef={passwordRef}
            autoComplete="new-password"
            helperText="8+ characters with upper and lower case, a number and a symbol"
            FormHelperTextProps={{ sx: { mx: 0.25, color: 'text.secondary' } }}
            required
          />
        </Stack>

        <Box sx={{ mt: 1.5 }}>
          <PersistLoginCheckbox persist={persist} setPersist={setPersist} />
        </Box>

        {persist && <PersistLoginAlert sx={{ mt: 1.5 }} />}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <HowToRegOutlined />}
          sx={{ ...goldButtonSx, mt: 2.5, height: 44, fontSize: '0.95rem', textTransform: 'none' }}
        >
          {isLoading ? 'Signing up…' : 'Sign up'}
        </Button>

        {error && (
          <AuthError>
            {error}
            {error === 'Password not strong enough' && (
              <ul>
                {PASSWORD_RULES.map((rule) => <li key={rule}>{rule}</li>)}
              </ul>
            )}
          </AuthError>
        )}
      </Box>

      <OrDivider />
      <SignInWithGoogleButton persist={persist} setPersist={setPersist} label="Sign up with Google" />

      <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '0.85rem', color: 'text.secondary' }}>
        Already have an account?{' '}
        <Link component={RouterLink} to="/login" sx={linkSx}>Log in</Link>
      </Typography>
    </AuthCard>
  )
}

export default Signup
